import { cardnexusFetch } from "@/lib/cardnexus/browser";
import { mergeLocalProductMap } from "@/lib/cardnexus/mapping";
import { replaceInventory, type InventoryEntry } from "@/lib/local-store";

export type CnImportLine = {
  inventoryId: string;
  productId: number;
  quantity: number;
  cardId: string | null;
  finish: string;
  condition?: string | null;
  language?: string | null;
  forSale?: boolean;
  name?: string | null;
  printNumber?: string | null;
  expansionCode?: string | null;
  expansionName?: string | null;
  imageUrl?: string | null;
};

export type CnImportResult = {
  entries: InventoryEntry[];
  matched: number;
  unmatched: number;
  count: number;
  productMap: Array<{ cardId: string; productId: number; inventoryId?: string }>;
  lines: CnImportLine[];
  mirror: Array<{
    cardId: string;
    quantity: number;
    productId: number;
    inventoryId?: string;
  }>;
};

/** Pull CardNexus inventory and mirror it into local RiftHub collection. */
export async function importCollectionFromCardnexus(
  opts: { refresh?: boolean } = {},
): Promise<CnImportResult> {
  const data = await cardnexusFetch<{
    mirror: Array<{
      cardId: string;
      quantity: number;
      productId: number;
      inventoryId?: string;
    }>;
    lines?: CnImportLine[];
    matched?: number;
    unmatched?: number;
    count?: number;
  }>(`/api/cardnexus/inventory${opts.refresh ? "?refresh=1" : ""}`);

  const mirror = data.mirror ?? [];
  const entries = mirror.map((m) => ({
    cardId: m.cardId,
    quantity: m.quantity,
  }));
  replaceInventory(entries);

  const productMap = mirror.map((m) => ({
    cardId: m.cardId,
    productId: m.productId,
    inventoryId: m.inventoryId,
  }));
  mergeLocalProductMap(productMap);

  return {
    entries,
    matched: data.matched ?? mirror.length,
    unmatched: data.unmatched ?? 0,
    count: data.count ?? 0,
    productMap,
    lines: data.lines ?? [],
    mirror,
  };
}
